import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useZakatGlobal } from '../../context/ZakatGlobalContext';
import QuickZakatCalculator from './QuickZakatCalculator';
import { IntentQuiz } from '../IntentQuiz';

/**
 * HeroBento — Amanah 2.0 hero (Feb 2026 rebuild).
 *
 * Guiding rule: a first-time visitor MUST be able to calculate their
 * Zakat without making a single navigation choice. The Quick Zakat card
 * is therefore ALWAYS the hero. No prior tap, no persona picker, no
 * long CTA row sits between arrival and the first input field.
 *
 * Desktop (lg+): two-column bento — copy on the left, live calculator
 *   on the right, above the fold.
 * Mobile: stacked — H1, one-line sub, live calculator immediately.
 *
 * Removed vs the previous hero (per rebuild spec):
 *   • 5-button CTA row (Choose your journey / I'm not sure / Learn /
 *     Explore by country / Succession) — replaced with a single small
 *     text link under the calculator.
 *   • 4-item trust strip → replaced with one plain caption line.
 *   • 5-column vanity stats (2B+ / 95 / 15 / 100% / 0) — removed
 *     entirely; they are not product facts and pushed the tool below
 *     the fold.
 */
export const HeroBento = ({ onOpenFullCalculator, showQuickCalc = true }) => {
  const navigate = useNavigate();
  const { selectedPersonType, setSelectedPersonType, setCurrentStep } = useZakatGlobal();
  const [quizOpen, setQuizOpen] = useState(false);

  const openFullCalc = () => {
    if (!selectedPersonType) setSelectedPersonType('salaried');
    setCurrentStep(2);
    if (onOpenFullCalculator) onOpenFullCalculator();
    navigate('/zakat/calculator');
  };

  return (
    <section
      data-testid="hero-bento"
      className="gd-anchor relative bg-[#EDF5F1] text-[#0D1520] border-b border-[#CBD2D9]"
    >
      <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-8 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* LEFT — Copy */}
          <div className={showQuickCalc ? 'lg:col-span-6 min-w-0' : 'lg:col-span-12 min-w-0'}>
            <h1
              className="font-heading text-[28px] md:text-[32px] lg:text-[40px] leading-[1.1] tracking-tight"
              data-testid="hero-bento-headline"
            >
              Your wealth is an <span className="text-gold-400">Amanah</span>.
              <br className="hidden sm:block" />
              Know it. Plan it. Pass it on rightly.
            </h1>

            <p className="mt-4 text-[15px] md:text-[16px] text-[#4A5568] leading-relaxed max-w-lg">
              Private tools for Zakat, inheritance and family legacy.
            </p>

            <p className="mt-5 text-[13px] text-[#8B8172]">
              Private by design · no account · nothing stored.
            </p>
          </div>

          {/* RIGHT — Live Quick Calculator */}
          {showQuickCalc && (
            <div className="lg:col-span-6 min-w-0" data-testid="hero-bento-calc">
              <QuickZakatCalculator onOpenFull={openFullCalc} testid="hero-quick-calc" />
              <div className="mt-3 text-center lg:text-left">
                <button
                  type="button"
                  onClick={() => setQuizOpen(true)}
                  data-testid="hero-bento-cta-quiz"
                  className="text-[13px] text-[#4A5568] hover:text-gold-300 underline underline-offset-4 decoration-white/20 hover:decoration-gold-400 transition-colors"
                >
                  Not sure where to start?
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <IntentQuiz open={quizOpen} onOpenChange={setQuizOpen} />
    </section>
  );
};

export default HeroBento;
